import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";

export const PerformanceContainer = styled(Box)(({ theme }) => ({
  padding: "50px",
  backgroundColor: theme.palette.background.default,
  height: "calc(100vh - 200px)",
  overflowY: "auto",
  overflowX: "hidden",
  position: "relative",
}));

export const MetricsGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: "30px",
  marginTop: theme.spacing(2),
  overflow: "visible",
  "& > *": {
    flex: "0 0 auto",
    minWidth: "230px",
  },
}));

export const StyledMetricCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2.5),
  minHeight: "199px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  gap: theme.spacing(0.5),
  position: "relative",
  overflow: "hidden",
  transition: "box-shadow 0.2s ease-in-out, transform 0.1s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[2],
    transform: "translateY(-1px)",
  },
}));

export const MetricTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 700,
  color: theme.palette.text.secondary,
  letterSpacing: "0.02em",
  lineHeight: 1.2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
}));

export const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: "1.75rem",
  fontWeight: 700,
  color: theme.palette.text.primary,
  lineHeight: 1.2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
}));

export const ChangeContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isGoodChange",
})<{ isGoodChange?: boolean }>(({ theme, isGoodChange }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  color: isGoodChange
    ? theme.palette.neutral.green
    : isGoodChange === false
      ? theme.palette.neutral.red
      : theme.palette.text.secondary,
}));

export const ChangeIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isGoodChange",
})<{ isGoodChange?: boolean }>(({ theme, isGoodChange }) => ({
  fontSize: "1rem",
  display: "flex",
  alignItems: "center",
  color: isGoodChange
    ? theme.palette.neutral.green
    : isGoodChange === false
      ? theme.palette.neutral.red
      : theme.palette.text.secondary,
}));

export const ChangeText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isGoodChange",
})<{ isGoodChange?: boolean }>(({ theme, isGoodChange }) => ({
  fontSize: "0.875rem",
  fontWeight: 600,
  lineHeight: 1,
  color: isGoodChange
    ? theme.palette.neutral.green
    : isGoodChange === false
      ? theme.palette.neutral.red
      : theme.palette.text.secondary,
}));

export const FlexCenter = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const PieChartContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: "30px",
  minHeight: "320px",
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  transition: "box-shadow 0.2s ease-in-out, transform 0.1s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[2],
    transform: "translateY(-1px)",
  },
}));

export const PieChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textAlign: "center",
}));

export const PieChartContent = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flex: 1,
  gap: "16px",
}));

export const PieChartWrapper = styled(Box)(() => ({
  flex: "0 0 200px",
  height: "200px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const PieChartLegend = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  paddingLeft: theme.spacing(2),
}));

export const LegendItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 0),
}));

export const LegendColor = styled(Box)<{ color: string }>(({ color }) => ({
  width: "12px",
  height: "12px",
  borderRadius: "2px",
  backgroundColor: color,
  flexShrink: 0,
}));

export const LegendText = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.primary,
  flex: 1,
}));

export const ChartsGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "40px",
  margin: "40px 0 50px 0",
}));

export const ProductsGrid = styled(Box)(({ theme }) => ({
  padding: "10px",
  border: `1px solid ${theme.palette.neutral.black}`,
  borderRadius: theme.shape.borderRadius,
  marginTop: "20px",
}));

export const StyledDiv = styled("div")(({ theme }) => ({
  "&.progress": {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "200px",
    minHeight: "200px",
  },
}));

export const MainSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gridGap: "24px",
}));

export const KPIProductsGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gridGap: "24px",
  marginTop: "2%",
  marginLeft: "6%",
  marginBottom: "2%",
}));

export const AnalysisContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
}));

export const LoadingSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

export const AnalysisHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  paddingBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const AnalysisTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

export const AnalysisMetaInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  flexWrap: "wrap",
  "& .MuiTypography-root": {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

export const MarkdownContent = styled(Box)(({ theme }) => ({
  "& h1, & h2": {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: `2px solid ${theme.palette.divider}`,
    lineHeight: 1.3,
    "&:first-child": {
      marginTop: 0,
    },
  },
  "& h3": {
    fontSize: "1.125rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    lineHeight: 1.4,
  },
  "& p": {
    fontSize: "1rem",
    color: theme.palette.text.primary,
    lineHeight: 1.8,
    marginBottom: theme.spacing(2.5),
    whiteSpace: "pre-wrap",
  },
  "& ul": {
    listStyleType: "disc",
    paddingLeft: theme.spacing(4),
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  "& ol": {
    listStyleType: "decimal",
    paddingLeft: theme.spacing(4),
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  "& li": {
    fontSize: "1rem",
    color: theme.palette.text.primary,
    lineHeight: 1.8,
    marginBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(1),
  },
  "& strong, & b": {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  "& em, & i": {
    fontStyle: "italic",
  },
  "& code": {
    backgroundColor: theme.palette.action.hover,
    padding: "3px 8px",
    borderRadius: "4px",
    fontSize: "0.9em",
    fontFamily: "monospace",
    color: theme.palette.text.primary,
  },
  "& pre": {
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius,
    overflow: "auto",
    marginBottom: theme.spacing(3),
    "& code": {
      padding: 0,
      backgroundColor: "transparent",
    },
  },
  "& blockquote": {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(2.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginLeft: 0,
    marginBottom: theme.spacing(3),
    fontStyle: "italic",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
  },
  "& a": {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  "& hr": {
    border: "none",
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
}));

export const LineChartContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: "30px",
  minHeight: "320px",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  transition: "box-shadow 0.2s ease-in-out, transform 0.1s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[2],
    transform: "translateY(-1px)",
  },
}));

export const LineChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textAlign: "center",
  marginBottom: theme.spacing(2),
}));

export const LineChartContent = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: "200px",
}));

export const LineChartWrapper = styled(Box)(() => ({
  flex: 1,
  minHeight: "200px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const LineChartLegend = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(3),
  marginTop: theme.spacing(2),
}));

export const ChartTooltipContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.neutral.white,
  border: "1px solid #ccc",
  borderRadius: "4px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  padding: "2px 5px",
  fontSize: "12px",
  color: theme.palette.neutral.darkCharcoal,
}));

export const ChartTooltipLabel = styled("span")(() => ({
  fontWeight: "bold",
}));

export const ChartTooltipValue = styled("span")(() => ({
  fontWeight: "normal",
}));

export const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: `${theme.shape.borderRadius}px !important`,
  marginBottom: theme.spacing(2),
  boxShadow: "none",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: `0 0 ${theme.spacing(2)} 0`,
  },
  "&:hover": {
    boxShadow: theme.shadows[1],
  },
}));

export const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: `${theme.shape.borderRadius}px`,
  minHeight: "64px",
  padding: theme.spacing(0, 2),
  "&.Mui-expanded": {
    minHeight: "64px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  },
  "& .MuiAccordionSummary-content": {
    margin: `${theme.spacing(2)} 0`,
    "&.Mui-expanded": {
      margin: `${theme.spacing(2)} 0`,
    },
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: theme.palette.text.secondary,
    "&.Mui-expanded": {
      color: theme.palette.primary.main,
    },
  },
}));

export const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
}));

export const AccordionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.25rem",
  fontWeight: 700,
  color: theme.palette.text.primary,
  lineHeight: 1.3,
}));

export const styles = () => {
  return {
    bodyWrap: {
      display: "flex !important",
      flexDirection: "row !important",
      alignItems: "center",
      justifyContent: "center",
    },
  };
};

export const SetGoalContentWrapper = styled("div")({
  padding: "16px 0",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
});

export const GoalInputContainer = styled("div")({
  display: "flex",
  alignItems: "center",
});

export const GoalLabel = styled(Typography)({
  fontWeight: 700,
  minWidth: "55px",
  fontSize: "16px",
});

export const GoalActionsWrapper = styled("div")({
  display: "flex",
  gap: "8px",
  justifyContent: "flex-end",
  width: "100%",
  paddingRight: "16px",
});

export const GoalLoadingOverlay = styled("div")({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  zIndex: 1,
});

export const MetricCardHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  width: "100%",
});

export const MetricMenuButton = styled(IconButton)({
  padding: "4px",
  marginTop: "-4px",
  marginRight: "-8px",
});
